import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import { useToast } from '../context/ToastContext.js';
import './CoachGoalReview.css';

const ENCOURAGEMENT_TYPES = [
  { id: 'great_job',  emoji: '🌟', label: 'Great Job!' },
  { id: 'keep_going', emoji: '💪', label: 'Keep Going' },
  { id: 'milestone',  emoji: '🎉', label: 'Milestone!' },
  { id: 'suggestion', emoji: '💡', label: 'Suggestion' },
  { id: 'general',    emoji: '📝', label: 'General' },
];

const APPARATUS = [
  { id: 'vault',      label: 'Vault',        emoji: '🏃', color: '#FF6B6B' },
  { id: 'floor',      label: 'Floor',        emoji: '🤸', color: '#4ECDC4' },
  { id: 'bars',       label: 'Uneven Bars',  emoji: '💪', color: '#FFE66D' },
  { id: 'beam',       label: 'Balance Beam', emoji: '🧘', color: '#95E1D3' },
  { id: 'trampoline', label: 'Trampoline',   emoji: '⛹️', color: '#A8E6CF' },
  { id: 'general',    label: 'General',      emoji: '⭐', color: '#9D84B7' },
];

const DIFFICULTY = {
  beginner:     { label: 'Beginner',     icon: '🟢' },
  intermediate: { label: 'Intermediate', icon: '🟡' },
  advanced:     { label: 'Advanced',     icon: '🟠' },
  elite:        { label: 'Elite',        icon: '🔴' },
};

const apparatusFor = (c) => APPARATUS.find(a => a.id === c) || APPARATUS[5];
const isAchieved = (g) => g.status === 'achieved' || g.status === 'exceeded';

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const diff = new Date(dateStr + 'T00:00:00') - new Date(new Date().toDateString());
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function initials(name = '') {
  return name.trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') || '?';
}

function DueChip({ goal }) {
  if (!goal.target_date || isAchieved(goal)) return null;
  const d = daysUntil(goal.target_date);
  if (d === null) return null;
  if (d < 0) return <span className="cgr-due overdue">Overdue</span>;
  if (d === 0) return <span className="cgr-due due-today">Due Today</span>;
  if (d <= 7) return <span className="cgr-due due-soon">Due {d}d</span>;
  return null;
}

function CoachGoalReview() {
  const toast = useToast();
  const [athletes, setAthletes] = useState([]);
  const [goals, setGoals] = useState([]);            // ALL team goals
  const [selectedAthleteId, setSelectedAthleteId] = useState('all');
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [expandedGoalId, setExpandedGoalId] = useState(null);
  const [feedbackByGoal, setFeedbackByGoal] = useState({});

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [athletesRes, goalsRes] = await Promise.all([
        api.get(`${API_BASE_URL}/athletes`),
        api.get(`${API_BASE_URL}/api/v1/goals`),   // coach → all team goals
      ]);
      setAthletes(athletesRes.data || []);
      setGoals(Array.isArray(goalsRes.data) ? goalsRes.data : (goalsRes.data?.goals || []));
    } catch (err) {
      console.error('Error loading goals:', err);
      toast('Error loading team goals', 'error');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const updateStatus = async (goalId, status) => {
    try {
      await api.put(`${API_BASE_URL}/api/v1/goals/${goalId}`, { status });
      setGoals(prev => prev.map(g => g.id === goalId
        ? { ...g, status, progress_percentage: status === 'achieved' || status === 'exceeded' ? 100 : g.progress_percentage }
        : g));
      toast('Goal updated ✅', 'success');
    } catch {
      toast('Failed to update goal', 'error');
    }
  };

  const loadFeedback = async (goalId) => {
    try {
      const res = await api.get(`${API_BASE_URL}/api/v1/goals/${goalId}`);
      setFeedbackByGoal(prev => ({ ...prev, [goalId]: res.data.feedback || [] }));
    } catch {}
  };

  const toggleExpand = (goalId) => {
    if (expandedGoalId === goalId) { setExpandedGoalId(null); return; }
    setExpandedGoalId(goalId);
    if (feedbackByGoal[goalId] === undefined) loadFeedback(goalId);
  };

  const sendFeedback = async (goalId, text, type) => {
    if (!text.trim()) { toast('Please enter feedback', 'warning'); return; }
    try {
      await api.post(`${API_BASE_URL}/api/v1/goals/${goalId}/feedback`, { feedback: text, encouragement_type: type });
      toast('Encouragement sent! 🎉', 'success');
      loadFeedback(goalId);
    } catch {
      toast('Failed to send feedback', 'error');
    }
  };

  // Per-athlete active-goal counts for the pill badges
  const activeCountFor = (athleteId) =>
    goals.filter(g => g.athlete_id === athleteId && !isAchieved(g)).length;

  // Visible goals after all filters
  const visibleGoals = goals.filter(g => {
    if (selectedAthleteId !== 'all' && g.athlete_id !== Number(selectedAthleteId)) return false;
    if (filterStatus !== 'all' && g.status !== filterStatus) return false;
    if (filterType !== 'all' && g.goal_type !== filterType) return false;
    return true;
  });

  // Team-wide summary (respects the athlete filter, ignores status/type filters)
  const scopeGoals = selectedAthleteId === 'all'
    ? goals
    : goals.filter(g => g.athlete_id === Number(selectedAthleteId));
  const summary = {
    total: scopeGoals.length,
    achieved: scopeGoals.filter(isAchieved).length,
    inProgress: scopeGoals.filter(g => g.status === 'in_progress').length,
    dueSoon: scopeGoals.filter(g => {
      const d = daysUntil(g.target_date);
      return d !== null && d >= 0 && d <= 7 && !isAchieved(g);
    }).length,
  };

  if (loading) {
    return <div className="cgr-loading"><div className="cgr-spinner" />Loading team goals…</div>;
  }

  return (
    <div className="cgr-page">
      <div className="cgr-header">
        <div>
          <h1 className="cgr-title">Team Goals</h1>
          <p className="cgr-subtitle">Track progress and cheer on your athletes</p>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="cgr-summary">
        <div className="cgr-tile">
          <span className="cgr-tile-num">{summary.total}</span>
          <span className="cgr-tile-label">Total Goals</span>
        </div>
        <div className="cgr-tile tile-green">
          <span className="cgr-tile-num">{summary.achieved}</span>
          <span className="cgr-tile-label">Achieved</span>
        </div>
        <div className="cgr-tile tile-orange">
          <span className="cgr-tile-num">{summary.inProgress}</span>
          <span className="cgr-tile-label">In Progress</span>
        </div>
        <div className="cgr-tile tile-red">
          <span className="cgr-tile-num">{summary.dueSoon}</span>
          <span className="cgr-tile-label">Due This Week</span>
        </div>
      </div>

      {/* Athlete strip */}
      <div className="cgr-athlete-strip">
        <button
          className={`cgr-pill ${selectedAthleteId === 'all' ? 'active' : ''}`}
          onClick={() => setSelectedAthleteId('all')}
        >
          <span className="cgr-pill-avatar all-avatar">👥</span>
          <span className="cgr-pill-name">All Athletes</span>
          <span className="cgr-pill-badge">{goals.filter(g => !isAchieved(g)).length}</span>
        </button>
        {athletes.map(a => {
          const count = activeCountFor(a.id);
          return (
            <button
              key={a.id}
              className={`cgr-pill ${selectedAthleteId === String(a.id) ? 'active' : ''}`}
              onClick={() => setSelectedAthleteId(String(a.id))}
            >
              <span className="cgr-pill-avatar">{initials(a.name)}</span>
              <span className="cgr-pill-name">{a.name}</span>
              {count > 0 && <span className="cgr-pill-badge">{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="cgr-filters">
        <select value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="all">All Types</option>
          <option value="short_term">Short-Term</option>
          <option value="long_term">Long-Term</option>
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="all">All Statuses</option>
          <option value="not_started">Not Started</option>
          <option value="in_progress">In Progress</option>
          <option value="achieved">Achieved</option>
          <option value="exceeded">Exceeded</option>
        </select>
        <span className="cgr-result-count">{visibleGoals.length} goal{visibleGoals.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Goal grid */}
      {visibleGoals.length === 0 ? (
        <div className="cgr-empty">
          <div className="cgr-empty-icon">🎯</div>
          <h3>No goals to show</h3>
          <p>{selectedAthleteId === 'all'
            ? 'No athletes have set goals matching these filters yet.'
            : 'This athlete has no goals matching these filters.'}</p>
        </div>
      ) : (
        <div className="cgr-grid">
          {visibleGoals.map(goal => (
            <CoachGoalCard
              key={goal.id}
              goal={goal}
              showAthlete={selectedAthleteId === 'all'}
              expanded={expandedGoalId === goal.id}
              feedback={feedbackByGoal[goal.id]}
              onToggle={() => toggleExpand(goal.id)}
              onStatus={(s) => updateStatus(goal.id, s)}
              onSendFeedback={(text, type) => sendFeedback(goal.id, text, type)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CoachGoalCard({ goal, showAthlete, expanded, feedback, onToggle, onStatus, onSendFeedback }) {
  const app = apparatusFor(goal.category);
  const achieved = isAchieved(goal);
  const [fbText, setFbText] = useState('');
  const [fbType, setFbType] = useState('great_job');

  const statusLabel = { not_started: 'Not Started', in_progress: 'In Progress', achieved: 'Achieved', exceeded: 'Exceeded!' };
  const statusClass = { not_started: 'st-grey', in_progress: 'st-orange', achieved: 'st-green', exceeded: 'st-purple' };
  const diff = DIFFICULTY[goal.difficulty_level] || {};

  const handleSend = () => { onSendFeedback(fbText, fbType); setFbText(''); };

  return (
    <div className={`cgr-card ${achieved ? 'cgr-card-achieved' : ''} ${expanded ? 'cgr-card-expanded' : ''}`}>
      <div className="cgr-card-top" style={{ backgroundColor: app.color }} onClick={onToggle}>
        <span className="cgr-card-emoji">{achieved ? '🏆' : app.emoji}</span>
        <div className="cgr-card-top-info">
          <span className="cgr-card-cat">{app.label}</span>
          <span className="cgr-card-type">{goal.goal_type === 'short_term' ? '📅 Short-Term' : '🏆 Long-Term'}</span>
        </div>
        <DueChip goal={goal} />
      </div>

      <div className="cgr-card-body" onClick={onToggle}>
        {showAthlete && (
          <div className="cgr-athlete-tag">
            <span className="cgr-athlete-dot" style={{ background: app.color }}>{initials(goal.athlete_name)}</span>
            {goal.athlete_name}
          </div>
        )}
        <h3 className="cgr-card-title">{goal.title}</h3>
        {goal.description && <p className="cgr-card-measure"><span>Measure:</span> {goal.description}</p>}

        <div className="cgr-card-meta">
          <span className={`cgr-status ${statusClass[goal.status] || ''}`}>{statusLabel[goal.status] || goal.status}</span>
          {diff.label && <span className="cgr-chip">{diff.icon} {diff.label}</span>}
          {goal.target_date && (
            <span className="cgr-chip cgr-chip-date">📅 {new Date(goal.target_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
          )}
        </div>

        <div className="cgr-progress-track">
          <div className="cgr-progress-fill" style={{ width: `${goal.progress_percentage}%`, backgroundColor: achieved ? '#27AE60' : app.color }} />
        </div>
        <p className="cgr-progress-pct">{goal.progress_percentage}% complete</p>
      </div>

      {expanded && (
        <div className="cgr-card-drawer">
          {/* Status controls */}
          <div className="cgr-section-label">Update Status</div>
          <div className="cgr-status-btns">
            {['not_started', 'in_progress', 'achieved', 'exceeded'].map(s => (
              <button
                key={s}
                className={`cgr-status-btn ${goal.status === s ? 'active' : ''}`}
                onClick={() => onStatus(s)}
              >
                {statusLabel[s]}
              </button>
            ))}
          </div>

          {/* Feedback composer */}
          <div className="cgr-section-label">Send Encouragement</div>
          <div className="cgr-enc-row">
            {ENCOURAGEMENT_TYPES.map(t => (
              <button
                key={t.id}
                className={`cgr-enc-btn ${fbType === t.id ? 'active' : ''}`}
                title={t.label}
                onClick={() => setFbType(t.id)}
              >
                {t.emoji}
              </button>
            ))}
          </div>
          <textarea
            className="cgr-fb-textarea"
            placeholder="Write an encouraging note…"
            value={fbText}
            onChange={e => setFbText(e.target.value)}
            rows="2"
          />
          <button className="cgr-fb-send" onClick={handleSend} style={{ background: app.color }}>Send 💬</button>

          {/* Existing feedback */}
          {feedback === undefined ? (
            <p className="cgr-fb-loading">Loading feedback…</p>
          ) : feedback.length > 0 && (
            <div className="cgr-fb-list">
              {feedback.map((f, i) => (
                <div key={i} className="cgr-fb-item">
                  <div className="cgr-fb-meta">
                    <span className="cgr-fb-coach">{f.coach_name || 'Coach'}</span>
                    <span className="cgr-fb-type">{ENCOURAGEMENT_TYPES.find(t => t.id === f.type)?.emoji || '📝'}</span>
                  </div>
                  <p className="cgr-fb-text">{f.feedback}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CoachGoalReview;
